/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useUniqueId } from '@squiz/dxp-console-support';
import clsx from 'clsx';
import React, { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DXP_ACTIVE_CLASS } from '../../Constants/Design';
import { Tab } from './Tabs.defs';
import { TabsProps } from './Tabs.props';

/**
 * SDS Tabs component.
 */
export const Tabs = (props: TabsProps): JSX.Element => {
  const { activeTabIndex, classes, listLabel, setActiveTabIndex, tabAction, tabList, ...rest } = props;

  const [activeTab, setActiveTab] = useState<Tab>(tabList[0]);
  const currentFocusIndex = useRef<number>(0);

  const uniqueId = useUniqueId();
  const tabIdPrefix = `sds-tab`;
  const tabContentId = `sds-tabs--${uniqueId}`;
  const tabClass = clsx(`sds-tabs`, classes);

  // Create tab refs
  const tabRefs = useMemo<Array<React.RefObject<HTMLButtonElement>>>(
    () =>
      Array.from(
        {
          length: tabList.length,
        },
        () => createRef<HTMLButtonElement>(),
      ),
    [tabList],
  );

  // Activate a tab
  const updateTab = useCallback(
    (newTabIndex: number): void => {
      currentFocusIndex.current = newTabIndex;
      setActiveTab(tabList[newTabIndex]);
    },
    [currentFocusIndex, setActiveTab, tabList],
  );

  // Handles moving the focus between tabs
  const moveTabFocus = useCallback(
    (itemIndex: number): void => {
      currentFocusIndex.current = itemIndex;
      const tab = tabRefs[itemIndex].current as HTMLButtonElement;
      tab.focus();
    },
    [currentFocusIndex, tabRefs],
  );

  // Handles keyboard events that occur on tabs
  const handleTabKeydown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
    const { key } = event;

    // Handle only these keys
    const navigationKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (navigationKeys.includes(key)) {
      // Create mutable value that initializes as the currentFocusIndex value
      let newFocusIndex = currentFocusIndex.current;

      // Controls the new index to focus
      if (key === 'ArrowUp' || key === 'ArrowLeft') {
        newFocusIndex -= 1;
      } else if (key === 'ArrowDown' || key === 'ArrowRight') {
        newFocusIndex += 1;
      } else if (key === 'Home') {
        newFocusIndex = 0;
      } else if (key === 'End') {
        newFocusIndex = tabRefs.length - 1;
      }

      if (newFocusIndex > tabRefs.length - 1) {
        newFocusIndex = 0;
      } else if (newFocusIndex < 0) {
        newFocusIndex = tabRefs.length - 1;
      }

      // Set focus on the new value
      moveTabFocus(newFocusIndex);
      return;
    }
  };

  // Another reference to the active tab to maintain correct focus.
  let focusedTab = tabList[currentFocusIndex.current];
  // istanbul ignore next
  if (!focusedTab) {
    focusedTab = tabList[0];
  }

  const getTabs = (): Array<JSX.Element> => {
    return tabList.map((tab: Tab, index: number): JSX.Element => {
      return (
        <li
          className={clsx(`sds-tabs-item`, {
            [DXP_ACTIVE_CLASS]: tab.key === activeTab.key,
          })}
          key={index}
          role="none"
        >
          <button
            aria-controls={tabContentId}
            aria-selected={tab === activeTab || tab === focusedTab}
            id={`${tabIdPrefix}--${tab.key}`}
            onClick={(event): void => {
              event.preventDefault();
              setActiveTabIndex && setActiveTabIndex(index);
              updateTab(index);
            }}
            onKeyDown={(event): void => {
              handleTabKeydown(event);
            }}
            ref={tabRefs[index]}
            role="tab"
            tabIndex={currentFocusIndex.current === index ? 0 : -1}
          >
            {tab.label}
          </button>
        </li>
      );
    });
  };

  // Set the active tab when parent component has updated the active tab index
  useEffect(() => {
    if (!activeTabIndex) {
      return;
    }
    if (tabList[activeTabIndex] !== activeTab) {
      updateTab(activeTabIndex);
    }
  }, [activeTab, activeTabIndex, tabList, updateTab]);

  return (
    <div className={tabClass} {...rest}>
      <ul aria-label={listLabel} className="sds-tabs-list" role="tablist">
        {getTabs()}
        {tabAction && (
          <li className={`sds-tabs-item`} key={`tab-action`} role="none">
            {tabAction}
          </li>
        )}
      </ul>
      <div
        aria-labelledby={`${tabIdPrefix}--${activeTab.key}`}
        className="sds-tabs-content"
        id={tabContentId}
        role="tabpanel"
        tabIndex={0}
      >
        {focusedTab.content}
      </div>
    </div>
  );
};
